import type { z } from "zod";

import {
  ErrorResponseSchema,
  JoinCodeSchema,
  PlayerBootstrapSchema,
  PlayerStateResponseSchema,
  type ErrorResponse,
  type PlayerBootstrap,
  type PlayerStateResponse,
} from "@saboteur/contracts";

import { storeParticipantToken } from "../realtime/token-store";

export class HttpApiError extends Error {
  public constructor(
    public readonly status: number,
    public readonly detail: ErrorResponse,
  ) {
    super(detail.message);
    this.name = "HttpApiError";
  }
}

export type SaboteurHttpClient = ReturnType<typeof createHttpClient>;

async function parseResponse<Output>(
  response: Response,
  schema: z.ZodType<Output>,
): Promise<Output> {
  const body: unknown = await response.json().catch(() => undefined);
  if (!response.ok) {
    const error = ErrorResponseSchema.safeParse(body);
    throw new HttpApiError(
      response.status,
      error.success
        ? error.data
        : {
            code: "PERSISTENCE_FAILED",
            message: "The mine could not be reached. Please try again.",
          },
    );
  }

  return schema.parse(body);
}

export function createHttpClient(baseUrl = "/api/v1") {
  const request = (path: string, init?: RequestInit): Promise<Response> =>
    fetch(`${baseUrl}${path}`, {
      ...init,
      headers: { Accept: "application/json", ...init?.headers },
    });

  const rememberBootstrap = (bootstrap: PlayerBootstrap): PlayerBootstrap => {
    storeParticipantToken(bootstrap.joinCode, bootstrap.participantToken);
    return bootstrap;
  };

  return {
    async createRoom(displayName: string, avatarIndex?: number): Promise<PlayerBootstrap> {
      const response = await request("/rooms", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ displayName, avatarIndex }),
      });
      return rememberBootstrap(await parseResponse(response, PlayerBootstrapSchema));
    },

    async joinRoom(
      joinCode: string,
      displayName: string,
      avatarIndex?: number,
    ): Promise<PlayerBootstrap> {
      const code = JoinCodeSchema.parse(joinCode);
      const response = await request(`/rooms/${encodeURIComponent(code)}/players`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ displayName, avatarIndex }),
      });
      return rememberBootstrap(await parseResponse(response, PlayerBootstrapSchema));
    },

    async getPlayerState(joinCode: string, participantToken: string): Promise<PlayerStateResponse> {
      const code = JoinCodeSchema.parse(joinCode);
      const response = await request(`/rooms/${encodeURIComponent(code)}/state`, {
        headers: { Authorization: `Bearer ${participantToken}` },
      });
      return parseResponse(response, PlayerStateResponseSchema);
    },
  };
}

export const httpClient = createHttpClient();
